import { z } from "zod";
import { appointmentStatus, CANCELLATION_REASON_MAX_LENGTH, day, optional, storableText } from "./appointments";
import { partyKind, recordId } from "./contracts";

export const CHAT_MESSAGE_PAGE_SIZE = 30;
export const CHAT_MESSAGE_PAGE_SIZE_MAX = 100;

const timestamp = z.string().datetime({ offset: true });

export const chatRoomKind = z.enum(["appointment", "direct", "contract"]);
export type ChatRoomKind = z.infer<typeof chatRoomKind>;

export const chatFileKind = z.enum(["image", "document", "audio", "video", "other"]);
export type ChatFileKind = z.infer<typeof chatFileKind>;

export const chatPerson = z.object({ id: recordId, name: z.string().nullable(), picture: z.string().nullable() });
export type ChatPerson = z.infer<typeof chatPerson>;

export const chatMessage = z.object({
  id: recordId,
  room: recordId,
  sender: chatPerson,
  text: z.string(),
  isSystem: z.boolean(),
  isFile: z.boolean(),
  fileKind: chatFileKind.nullable(),
  metaData: z.record(z.string(), z.unknown()).nullable(),
  sentAt: timestamp,
  updatedAt: timestamp,
});
export type ChatMessage = z.infer<typeof chatMessage>;

const named = z.object({ id: recordId, name: z.string() });

export const chatAppointment = z.object({
  id: recordId,
  status: appointmentStatus,
  date: z.string(),
  propertyName: z.string(),
  unitNumber: z.string().nullable(),
  service: named,
  vendor: named,
});
export type ChatAppointment = z.infer<typeof chatAppointment>;

export const chatContract = z.object({ id: recordId, property: named });
export type ChatContract = z.infer<typeof chatContract>;

export const chatRoom = z.object({
  id: recordId,
  kind: chatRoomKind,
  memberIds: z.array(recordId),
  directWith: chatPerson.nullable(),
  lastMessage: chatMessage.nullable(),
  updatedAt: timestamp,
  unreadCount: z.number().int().nonnegative(),
  lastReadAt: timestamp.nullable(),
  appointment: chatAppointment.nullable(),
  contract: chatContract.nullable(),
});
export type ChatRoom = z.infer<typeof chatRoom>;

export const chatRoomList = z.object({ items: z.array(chatRoom) });
export type ChatRoomList = z.infer<typeof chatRoomList>;

export const chatMessageListQuery = z
  .object({
    before: recordId.toLowerCase().optional(),
    limit: z.coerce.number().int().min(1).max(CHAT_MESSAGE_PAGE_SIZE_MAX).default(CHAT_MESSAGE_PAGE_SIZE),
  })
  .strict();
export type ChatMessageListQuery = z.infer<typeof chatMessageListQuery>;

export const chatMessagePage = z.object({ items: z.array(chatMessage), hasOlder: z.boolean() });
export type ChatMessagePage = z.infer<typeof chatMessagePage>;

export const chatMemberRole = z.enum(["worker"]);
export type ChatMemberRole = z.infer<typeof chatMemberRole>;

export const chatMember = chatPerson.extend({
  organization: z.object({ name: z.string(), kind: partyKind }).nullable(),
  roomRole: chatMemberRole.nullable(),
});
export type ChatMember = z.infer<typeof chatMember>;

export const chatMemberList = z.object({ items: z.array(chatMember) });
export type ChatMemberList = z.infer<typeof chatMemberList>;

export const CHAT_TEXT_MAX_LENGTH = 5000;
export const CHAT_FILE_NAME_MAX_LENGTH = 255;
export const CHAT_ANSWER_REASON_MAX_LENGTH = 1000;

const actingOrganization = optional(recordId.toLowerCase());

const mimeType = z
  .string()
  .max(255)
  .regex(/^[\w.+-]+\/[\w.+-]+$/, "expected a media type such as application/pdf");

export const chatFileUploadRequest = z.object({ name: storableText(CHAT_FILE_NAME_MAX_LENGTH), contentType: mimeType }).strict();
export type ChatFileUploadRequest = z.infer<typeof chatFileUploadRequest>;

export const chatFileUpload = z.object({ uploadUrl: z.string().url(), fileUrl: z.string().url() });
export type ChatFileUpload = z.infer<typeof chatFileUpload>;

const pixels = z.number().int().positive().max(100_000);

export const chatFile = z
  .object({
    url: z.string().url().max(2048),
    name: storableText(CHAT_FILE_NAME_MAX_LENGTH),
    mime: mimeType,
    size: z.number().int().nonnegative(),
    width: pixels.optional(),
    height: pixels.optional(),
  })
  .strict();
export type ChatFile = z.infer<typeof chatFile>;

export const chatApprovalRequest = z.discriminatedUnion("type", [
  z.object({ type: z.literal("reschedule"), newDate: day }).strict(),
  z.object({ type: z.literal("cancel"), reason: storableText(CANCELLATION_REASON_MAX_LENGTH) }).strict(),
  z.object({ type: z.literal("serviceChange"), service: recordId.toLowerCase() }).strict(),
]);
export type ChatApprovalRequest = z.infer<typeof chatApprovalRequest>;

export const chatMessageRequest = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("text"), text: storableText(CHAT_TEXT_MAX_LENGTH) }).strict(),
  z.object({ kind: z.literal("file"), file: chatFile }).strict(),
  z.object({ kind: z.literal("approvalRequest"), request: chatApprovalRequest, actingOrganization: recordId.toLowerCase() }).strict(),
  z
    .object({
      kind: z.literal("approvalAnswer"),
      request: recordId.toLowerCase(),
      approved: z.boolean(),
      reason: storableText(CHAT_ANSWER_REASON_MAX_LENGTH).optional(),
    })
    .strict(),
  z.object({ kind: z.literal("assignmentPrompt") }).strict(),
]);
export type ChatMessageRequest = z.infer<typeof chatMessageRequest>;
export type ChatMessageKind = ChatMessageRequest["kind"];

export const chatDirectRequest = z.object({ person: recordId.toLowerCase(), actingOrganization }).strict();
export type ChatDirectRequest = z.infer<typeof chatDirectRequest>;

export const chatMemberRequest = z.object({ person: recordId.toLowerCase(), actingOrganization }).strict();
export type ChatMemberRequest = z.infer<typeof chatMemberRequest>;

export const chatMemberAddition = z.object({ added: z.boolean(), room: chatRoom });
export type ChatMemberAddition = z.infer<typeof chatMemberAddition>;

export const chatContactListQuery = z.object({ actingOrganization }).strict();
export type ChatContactListQuery = z.infer<typeof chatContactListQuery>;

export const chatContact = z.object({
  id: recordId,
  name: z.string(),
  picture: z.string().nullable(),
  organization: z.object({ id: recordId, name: z.string(), kind: partyKind }),
});
export type ChatContact = z.infer<typeof chatContact>;

export const chatContactList = z.object({ items: z.array(chatContact) });
export type ChatContactList = z.infer<typeof chatContactList>;
